// @flow
import React, { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames/bind';
import inputStyle from '../../assets/styles/input.scss';
import { decimalRegex, emailRegex, urlRegex } from '../string';

export type FieldValidator = { [key: string]: FieldErrorValidator };
export type FieldErrorValidator = { [key: string]: string | (value: any) => boolean };
export type FormValidationResult = {
  field: string;
  errors: string[];
};
export type FormField = {
  value: any;
  errors?: string[];
};

// export const regexDecimal = /[0-9]{0,}(\.([0-9][0-9])?)/;

// Returns true if error
const includedValidators: { [key: string]: (value: any) => boolean } = {
  emailFormat: (value: string = ''): boolean => {
    const r = value.match(emailRegex);
    return !r || r[0] !== value;
  },
  required: (value: any): boolean => !value,
  urlFormat: (value: string = ''): boolean => {
    // If empty then no error
    if (!value) return false;
    if (value.startsWith('http')) return false;
    const r = value.match(urlRegex);
    return !r || r[0] !== value;
  },
  decimalFormat: (value: string = ''): boolean => {
    const r = value.match(decimalRegex);
    return !r || r[0] !== value;
  },
};

function getValidatorFn(validatorValue: string | (value: any) => boolean): (value: any) => boolean {
  if (typeof validatorValue === 'string') {
    const vfn = includedValidators[validatorValue];
    if (!vfn) {
      throw new Error(`Not existent included validator: ${validatorValue}`);
    }
    return vfn;
  } else return validatorValue;
}

export class FormValidator {
  validators: FieldValidator;

  constructor(validators: FieldValidator) {
    this.validators = validators;
  }

  replaceFieldValidators(validators: FieldValidator) {
    this.validators = { ...this.validators, ...validators };
  }

  validateField(field: string, value: any | any[], returnOnlyFirst: boolean = true): string[] {
    if (!this.validators[field]) {
      if (this.validators[field] !== null) { // We are not logging null validator to dynamically change it
        console.error(new Error(`No validator for field: ${field}`));
      }
      return [];
    }
    const result = [];
    const validatorFieldKeys = Object.keys(this.validators[field]);
    for (const validatorKey of validatorFieldKeys) {
      const validatorFn = getValidatorFn(this.validators[field][validatorKey]);
      if (validatorFn(value)) {
        result.push(validatorKey);
        if (returnOnlyFirst) break;
      }
    }
    return result;
  }

  validateForm(values: { [key: string]: any }, returnOnlyFirst: boolean = true): FormValidationResult[] {
    const validatorFieldsKeys = Object.keys(this.validators);
    const result = [];
    let firstResult;
    for (const validatorField of validatorFieldsKeys) {
      const fieldResult = this.validateField(validatorField, values[validatorField], returnOnlyFirst);
      if (fieldResult.length) {
        result.push({
          field: validatorField,
          errors: fieldResult,
        });
      }
    }
    return result;
  }

  static validateOnTheFly = (validator: FieldErrorValidator, value: any,
                             returnOnlyFirst: boolean = true): string[] => {
    const validatorFieldKeys = Object.keys(validator);
    const result = [];
    for (const validatorKey of validatorFieldKeys) {
      const validatorFn = getValidatorFn(validator[validatorKey]);
      if (validatorFn(value)) {
        result.push(validatorKey);
        if (returnOnlyFirst) break;
      }
    }
    return result;
  };

  static buildErrorLabel = (errors: string[] = [], dict: { [key: string]: string }, right?: Node) =>
    dict[errors[0]] && (
      <FormattedMessage id={dict[errors[0]]}>{
        txt => <label className={classNames(inputStyle.inputErrorLabel, {
          [inputStyle.yellow]: errors[0] === 'unscrapped',
        })}>{txt} {right}
        </label>
      }</FormattedMessage>);

  static flattenForm = (form: { [key: string]: { value: any } }): { [key: string]: any } =>
    Object.keys(form).reduce((acum: { [key: string]: any }, key: string) => ({
      ...acum,
      [key]: form[key].value,
    }), {});

  static getFlatValueFromField(formField?: FormField, defaultValue?: any): any {
    if (!formField) return defaultValue;
    return formField.value;
  }
}



// WEBPACK FOOTER //
// ./src/utils/form/index.js